import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import * as d3_xyzoom from 'd3-xyzoom';
import _ from 'lodash';
import styled from 'styled-components';

import LoadingSvg from 'common/images/LoadingSvg';
import * as gridUtils from '../utils/gridUtils';
import * as graphUtils from '../utils/graphUtils';
import { getXAxisLabel } from '../utils/xAxisLabel';
import * as defsUtils from '../utils/defsUtils';
import * as helpers from '../utils/helpers';
import { Plus, Minus } from '../../../images/FaIcons';
import InputField_T from '../../atoms/InputField';
import YAxisLimitModal from '../components/YAxisLimitModal';
import LineContext from '../components/LineContext';
import LineChartContainer from './LineChartContainer';
import UnlockedZoomSVG from '../../../images/UnlockedZoomSvg';
import LockedZoomSVG from '../../../images/LockedZoomSvg';
import EditIcon from '../../../images/BearingModal/EditIcon';
import ResetSvg from '../../../images/ResetSvg';

const ButtonContainer = styled.span`
  width: max-content;
  position: relative;
  left: -50px;
  top: 35px;
  display: flex;
  flex-direction: column;
`;

const MultiSetItem = styled(InputField_T)`
  position: absolute;
  background: transparent;
  ${props => props.left ? `left: ${props.left};` : 'left: -57px;'}
  border: none;
  cursor: pointer;
  padding: 0;
  box-shadow: none;
  z-index: 2;
  ${props => props.top ? `top: ${props.top};` : 'top: 0;'}
`;

const Loader = styled.div`
  top: 0;
  left: 0;
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  position: absolute;
  background-color: rgba(239,240,238,0.5);
`;

const SvgContainer = styled.span`
  width: max-content;
  &:hover {
    cursor: ${props => props.nohover ? 'default' : 'pointer'};
  }
  ${props => props.marginright && `margin-right: ${props.marginright};`}
  ${props => props.absolute && 'position: absolute;'}
  ${props => props.right && `right: ${props.right};`}
`;

const Button = styled.span`
  width: max-content;
  cursor: pointer;
  &:hover {
    ${props => props.disabled ? 'cursor: not-allowed;' : 'svg{color: black;}'}
  }
`;

const ButtonX = styled.span`
  position: absolute;
  background: transparent;
  bottom: -42px;
  cursor: pointer;
  right: 0px;
  border: none;
  padding: 0;
  box-shadow: none;
`;

const ButtonY = styled.span`
  position: absolute;
  background: transparent;
  ${props => props.left ? `left: ${props.left};` : 'left: -57px;'}
  border: none;
  cursor: pointer;
  padding: 0;
  box-shadow: none;
  z-index: 2;
  ${props => props.top ? `top: ${props.top};` : 'top: 0;'}
`;

// Chart type which d3 zoom should not be applied
const NoChartZoomTypes = [
  'overallAlarm',
  'utilization',
  'context'
];

const NoChartDblClickZoom = [
  'spectrum',
  'demod_spectrum'
];

const CHART_SETTINGS_STORAGE_KEY = 'chartSettings';
const Y_AXIS_SETTINGS_KEYS = {
  mode: 'yAxisLimitMode',
  percent: 'yAxisPercent',
  upperLimit: 'yAxisUpperLimit',
};

const readChartSettingsFromSession = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return {};
    const raw = window.sessionStorage.getItem(CHART_SETTINGS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

const mergeChartSettingsIntoSession = (patch) => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    const current = readChartSettingsFromSession();
    const next = { ...current, ...patch };
    window.sessionStorage.setItem(CHART_SETTINGS_STORAGE_KEY, JSON.stringify(next));
  } catch (e) {
    // ignore session storage failures (private mode, quota, etc.)
  }
};

export const LineChart = (WrappedComponent) => {
  class LineChartWrapper extends React.Component {
    constructor(props) {
      super(props);
      // Wrapped chart components may not provide a ref (e.g. function components without forwardRef).
      // Keep this defined so we can probe optional methods safely.
      this.refNode = {};
      this.setRef = this.setRef.bind(this);
      this.drawGraph = this.drawGraph.bind(this);
      this.redraw = this.redraw.bind(this);
      this.drawAxis = this.drawAxis.bind(this);
      this.drawTitle = this.drawTitle.bind(this);
      this.drawYlabel = this.drawYlabel.bind(this);
      this.drawSelection = this.drawSelection.bind(this);
      this.drawSelectionLines = this.drawSelectionLines.bind(this);
      this.drawDataLines = this.drawDataLines.bind(this);
      this.drawPoints = this.drawPoints.bind(this);
      this.zoomHandler = this.zoomHandler.bind(this);
      this.addMouseoverEventListener = this.addMouseoverEventListener.bind(this);
      this.addClickEventListener = this.addClickEventListener.bind(this);
      this.addDoubleClickEventListener = this.addDoubleClickEventListener.bind(this);
      this.changeYAxisDomain = this.changeYAxisDomain.bind(this);
      this.drawXlabel = this.drawXlabel.bind(this);

      const sessionSettings = readChartSettingsFromSession();
      const supportsYAxisLimit = props.type !== 'context';
      const preferredMode = supportsYAxisLimit
        ? ((sessionSettings && sessionSettings[Y_AXIS_SETTINGS_KEYS.mode]) || 'absolute')
        : 'absolute';
      const preferredPercent = supportsYAxisLimit
        ? ((sessionSettings && sessionSettings[Y_AXIS_SETTINGS_KEYS.percent]) || '150')
        : '150';
      const preferredUpperLimit = supportsYAxisLimit
        ? (sessionSettings && sessionSettings[Y_AXIS_SETTINGS_KEYS.upperLimit])
        : null;
      const initialYAxisUpperLimit = (preferredMode === 'absolute' && preferredUpperLimit != null && preferredUpperLimit !== '')
        ? preferredUpperLimit
        : '';

      // When true, componentDidUpdate has scheduled a deferred Y-axis apply for a data change;
      // enableZoom's callback should skip applying to avoid double application and flicker.
      this.yAxisApplyScheduledFromDataChange = false;

      this.state = {
        decreaseDisabled: true,
        axisLocked: props.axisLocked || { x: false, y: true },
        yAxisUpperLimit: initialYAxisUpperLimit,
        yAxisLimitMode: preferredMode, // 'absolute' | 'percent'
        yAxisPercent: `${preferredPercent}`,
        transformY: 0,
        transformX: 0,
        kx: 1,
        ky: 1,
        resetActive: false,
        contextHeight: this.props.height === '400px' ? '50px' : '25px',
        yAxisModalSnapshot: null // { mode, yAxisUpperLimit, yAxisPercent } when modal opens
      };
    }

    shouldApplyYAxisLimitToChart = () => this.props.type !== 'context';

    formatAxisLimitNumber = (value) => {
      if (!Number.isFinite(value)) return '';
      const trimTrailingZeros = s => s.replace(/(?:\.0+|(\.\d+?)0+)$/, '$1');
      const abs = Math.abs(value);
      if (abs >= 10) return trimTrailingZeros(value.toFixed(2));
      if (abs >= 1) return trimTrailingZeros(value.toFixed(2));
      if (abs >= 0.1) return trimTrailingZeros(value.toFixed(3));
      return trimTrailingZeros(value.toFixed(4));
    };

    // Keep bound validation aligned with what users see in the modal note.
    normalizeAxisLimitForValidation = (value) => {
      if (!Number.isFinite(value)) return value;
      const normalized = Number.parseFloat(this.formatAxisLimitNumber(value));
      return Number.isFinite(normalized) ? normalized : value;
    };

    isValueOutsideYAxisBounds = (value, options = {}) => {
      const { enforceMin = true, enforceMax = true } = options;
      const normalizedValue = this.normalizeAxisLimitForValidation(value);
      const normalizedMax = this.normalizeAxisLimitForValidation(this.state.maxYdomain);
      const normalizedMin = this.normalizeAxisLimitForValidation(this.state.minYdomain);

      const aboveMax = enforceMax
        && Number.isFinite(normalizedMax)
        && normalizedValue > normalizedMax;
      const belowMin = enforceMin
        && Number.isFinite(normalizedMin)
        && normalizedValue < normalizedMin;
      return aboveMax || belowMin;
    };

    // Peak of currently visible window (based on current x-domain) across active datasets.
    // Returns null if peak can't be computed.
    getPeakYInRange = (lo, hi) => {
      if (!this.props || !Array.isArray(this.props.data)) return null;
      const active = Array.isArray(this.props.activeData) ? this.props.activeData : [];
      let peak = -Infinity;
      this.props.data.forEach((dataset, idx) => {
        if (!Array.isArray(dataset) || dataset.length === 0) return;
        if (active.length && active[idx] === false) return;
        for (let i = 0; i < dataset.length; i += 1) {
          const p = dataset[i];
          if (!p) return;
          const xv = p.x && p.x.valueOf ? p.x.valueOf() : p.x;
          if (Number.isFinite(xv) && xv >= lo && xv <= hi) {
            const yv = p.y;
            if (Number.isFinite(yv) && yv > peak) peak = yv;
          }
        }
      });
      return (Number.isFinite(peak) && peak !== -Infinity) ? peak : null;
    };

    getVisiblePeakY = () => {
      if (!this.x) return null;
      const domain = this.x.domain && this.x.domain();
      if (!domain || domain.length < 2) return null;
      const d0 = domain[0] && domain[0].valueOf ? domain[0].valueOf() : domain[0];
      const d1 = domain[1] && domain[1].valueOf ? domain[1].valueOf() : domain[1];
      if (!Number.isFinite(d0) || !Number.isFinite(d1)) return null;
      return this.getPeakYInRange(Math.min(d0, d1), Math.max(d0, d1));
    };

    getGlobalPeakY = () => {
      if (!this.x2) return null;
      const domain = this.x2.domain && this.x2.domain();
      if (!domain || domain.length < 2) return null;
      const d0 = domain[0] && domain[0].valueOf ? domain[0].valueOf() : domain[0];
      const d1 = domain[1] && domain[1].valueOf ? domain[1].valueOf() : domain[1];
      if (!Number.isFinite(d0) || !Number.isFinite(d1)) return null;
      return this.getPeakYInRange(Math.min(d0, d1), Math.max(d0, d1));
    };

    applyPreferredPercentYAxisIfNeeded = () => {
      // Apply preferred % scaling after a redraw / data refresh so it persists across reloads/filters.
      if (!this.shouldApplyYAxisLimitToChart()) return;
      if (this.state.yAxisLimitMode !== 'percent') return;

      const pct = Number.parseFloat(this.state.yAxisPercent);
      if (!Number.isFinite(pct) || pct <= 0) return;

      const peak = this.getGlobalPeakY();
      if (!Number.isFinite(peak) || peak === null) return;

      const candidate = peak * (pct / 100);
      if (!Number.isFinite(candidate)) return;
      if (this.isValueOutsideYAxisBounds(candidate, { enforceMin: false })) return;

      this.changeYAxisScale(candidate);
    };

    applyPreferredAbsoluteYAxisIfNeeded = () => {
      // Apply saved absolute Y-axis limit after redraw / data refresh so it persists across reloads.
      if (!this.shouldApplyYAxisLimitToChart()) return;
      if (this.state.yAxisLimitMode !== 'absolute') return;

      const parsed = Number.parseFloat(this.state.yAxisUpperLimit);
      if (!Number.isFinite(parsed) || parsed <= 0) return;

      if (this.isValueOutsideYAxisBounds(parsed)) return;

      this.changeYAxisScale(parsed);
    };

    getCurrentYAxisUpperLimit = () => {
      try {
        if (this.y && typeof this.y.domain === 'function') {
          const yd = this.y.domain();
          if (Array.isArray(yd) && yd.length >= 2 && Number.isFinite(yd[1])) return yd[1];
        }
        if (this.y2 && typeof this.y2.domain === 'function') {
          const y2d = this.y2.domain();
          if (Array.isArray(y2d) && y2d.length >= 2 && Number.isFinite(y2d[1])) return y2d[1];
        }
        if (this.props && Array.isArray(this.props.range) && Number.isFinite(this.props.range[1])) return this.props.range[1];
      } catch (e) {
        // ignore
      }
      return null;
    };

    applyYAxisModeToChart = (mode) => {
      if (!this.shouldApplyYAxisLimitToChart()) return;
      if (mode === 'percent') {
        const pct = Number.parseFloat(this.state.yAxisPercent);
        if (!Number.isFinite(pct) || pct <= 0) {
          this.setState({ yAxisError: 'Please enter a valid percent (> 0)' });
          return;
        }
        const peak = this.getGlobalPeakY();
        if (!Number.isFinite(peak) || peak === null) {
          this.setState({ yAxisError: 'Peak is unavailable (no data)' });
          return;
        }
        const candidate = peak * (pct / 100);
        const { maxYdomain } = this.state;
        if (!Number.isFinite(candidate)) {
          this.setState({ yAxisError: 'Computed limit is not valid' });
          return;
        }
        if (this.isValueOutsideYAxisBounds(candidate, { enforceMin: false })) {
          this.setState({ yAxisError: `Computed limit must be ≤ ${this.formatAxisLimitNumber(maxYdomain)}` });
          return;
        }
        this.setState({ yAxisError: '' });
        this.changeYAxisScale(candidate);
        return;
      }

      // Absolute mode
      const raw = this.state.yAxisUpperLimit;
      const parsed = Number.parseFloat(raw);
      if (!Number.isFinite(parsed)) {
        const current = this.getCurrentYAxisUpperLimit();
        if (Number.isFinite(current)) {
          const nextText = this.formatAxisLimitNumber(current);
          this.setState({ yAxisUpperLimit: nextText, yAxisError: '' });
        } else {
          this.setState({ yAxisError: 'Not a valid number' });
        }
        return;
      }
      const { maxYdomain, minYdomain } = this.state;
      if (this.isValueOutsideYAxisBounds(parsed)) {
        this.setState({
          yAxisError: `Please enter a value between ${this.formatAxisLimitNumber(minYdomain)} and ${this.formatAxisLimitNumber(maxYdomain)}`
        });
        return;
      }
      this.setState({ yAxisError: '' });
      this.changeYAxisScale(parsed);
    };

    setYAxisLimitMode = (mode) => {
      mergeChartSettingsIntoSession({ [Y_AXIS_SETTINGS_KEYS.mode]: mode });
      const currentUpper = this.getCurrentYAxisUpperLimit();
      const nextUpperText = Number.isFinite(currentUpper) ? this.formatAxisLimitNumber(currentUpper) : null;
      this.setState((prev) => {
        const next = { yAxisLimitMode: mode };
        // Keep absolute input in sync with current axis so Save isn't blocked on mode switch.
        if (mode === 'absolute' && nextUpperText) next.yAxisUpperLimit = nextUpperText;
        // Clear any previous validation errors when switching modes.
        if (prev.yAxisError) next.yAxisError = '';
        return next;
      }, () => {
        // Switching modes should update chart bounds immediately to reflect the selected mode.
        this.applyYAxisModeToChart(mode);
      });
    };

    componentDidUpdate(prevProps) {
      if (prevProps.height !== this.props.height ||
        prevProps.width !== this.props.width ||
        !_.isEqual(prevProps.hierarchyViewPane, this.props.hierarchyViewPane)
      ) {
        this.drawGraph();
      }
      // Track if data changed so we can reapply percentage scaling after all updates.
      const dataChanged = prevProps.data && (!_.isEqual(prevProps.data, this.props.data));
      if (dataChanged) {
        this.yAxisApplyScheduledFromDataChange = true;
        this.drawGraph();
      }
      if (prevProps.range && !_.isEqual(prevProps.range, this.props.range)) {
        this.zoomApplied = false;
        this.drawGraph();
      }
      // When data changes (e.g., filter like 1-year applied), reapply saved Y-axis limit
      // once in a deferred manner. Skip applying in enableZoom's callback when this flag
      // is set to avoid double application and flicker.
      if (dataChanged) {
        if (!this.shouldApplyYAxisLimitToChart()) {
          this.yAxisApplyScheduledFromDataChange = false;
        } else if (this.state.yAxisLimitMode === 'percent') {
          setTimeout(() => {
            this.applyPreferredPercentYAxisIfNeeded();
            this.yAxisApplyScheduledFromDataChange = false;
          }, 0);
        } else if (this.state.yAxisLimitMode === 'absolute') {
          setTimeout(() => {
            this.applyPreferredAbsoluteYAxisIfNeeded();
            this.yAxisApplyScheduledFromDataChange = false;
          }, 0);
        } else {
          this.yAxisApplyScheduledFromDataChange = false;
        }
      }
      // For SpectrumChart.js temp x axis unit change
      if (this.props.xUnit && !_.isEqual(prevProps.xUnit, this.props.xUnit)) {
        this.drawGraph();
        // if (this.refNode.drawGraph) this.refNode.drawGraph(this);
        if (this.lineContext && this.lineContext.refNode.setBrushToSelection) {
          if (this.refNode.state.storeBrushSelection.length > 0) {
            this.lineContext.refNode.setBrushToSelection(this.refNode.state.storeBrushSelection);
          }
        }
        if (this.state.yScaleDomain) this.y.domain(this.state.yScaleDomain);
        this.redraw();
        // hack to restore brush to null on units change
        if (this.lineContext && this.x && this.refNode.state.storeBrushSelection && this.refNode.state.storeBrushSelection.length === 0 || _.isEqual(this.refNode.state.storeBrushSelection, this.x.range())) {
          setTimeout(() => {
            if (this.lineContext && this.lineContext.refNode) {
              this.lineContext.refNode.resetBrushSelection();
            }
          }, 0);
        }
      }
      if (prevProps.contextChart !== this.props.contextChart) {
        if (this.props.domain === undefined) {
          const domain = [];
          this.props.data.forEach((data) => {
            const dataDomain = d3.extent(data, d => d.x);
            if ((dataDomain[0] !== undefined && dataDomain[0] < domain[0]) || !domain[0]) domain[0] = dataDomain[0];
            if ((dataDomain[1] !== undefined && dataDomain[1] > domain[1]) || !domain[1]) domain[1] = dataDomain[1];
          });
          this.x.domain(domain);
        } else {
          this.x.domain(this.props.domain);
        }
        if (this.props.multiBrush && this.state.resetActive) this.props.redrawAllSpectrums();
        else this.redraw();
        if (this.props.type === 'multiline') {
          this.drawGraph();
        }
      } else if (!_.isEqual(prevProps.activeData, this.props.activeData)) {
        // redraw lines if active selection is changed
        this.props.activeData.forEach((active, idx) => {
          const opacity = active ? 1 : 0;
          d3.select(`.${this.props.chartName}.dataline.idx-${idx}`)
            .attr('opacity', opacity);
        });
      }
      if (!_.isEqual(this.props.selection, prevProps.selection)) {
        this.drawSelection();
      }

      if (this.props.axisLocked && !_.isEqual(prevProps.axisLocked, this.props.axisLocked)) {
        this.setState({
          axisLocked: this.props.axisLocked
        }, this.zoomAxis);
      }

      if (!_.isEqual(this.props.points, prevProps.points)) {
        this.drawPoints();
      }

      if (!_.isEqual(this.props.ylabel, prevProps.ylabel)) {
        this.drawYlabel();
      }

      if (!_.isEqual(this.props.colors, prevProps.colors)) {
        this.redrawDataLines();
      }

      if (prevProps.hoveredLegendItem !== this.props.hoveredLegendItem ||
        prevProps.activeLegendItem !== this.props.activeLegendItem) {
        this.redrawDataLines();
      }

      if (this.props.multiBrush && this.props.brushSelectionFromParent && !_.isEqual(this.props.brushSelectionFromParent, prevProps.brushSelectionFromParent)) {
        if (this.props.redrawAllSpectrums) {
          this.props.redrawSpectrum(this.props.index, this);
        }
      }
      if (this.props.transformFromParent && !_.isEqual(this.props.transformFromParent, prevProps.transformFromParent)) {
        const { transformX, transformY, kx, ky } = this.props.transformFromParent;
        const t = d3_xyzoom.xyzoomIdentity
          .translate(transformX, transformY)
          .scale(kx, ky);
        this.setState({
          transformX,
          transformY,
          kx,
          ky
        }, () => {
          const new_xScale = t.rescaleX(this.x2);
          const new_yScale = t.rescaleY(this.y2);
          this.y.domain(new_yScale.domain());
          this.x.domain(new_xScale.domain());
          this.redraw();
          if (this.zoom) d3.select(this.chart).call(this.zoom.transform, t);
        });
      }
      // TODO: in willreceive props ? and elsewhere ? draw a selection line
      // if (this.refNode.selection && this.props.selection) this.refNode.selection(this, this.props.selection);
      if (!NoChartZoomTypes.includes(this.props.type) && this.chart) {
        const transform = d3_xyzoom.xyzoomTransform(this.chart);
        if (!_.isEqual(transform, d3_xyzoom.xyzoomIdentity) && this.state.resetActive !== true) this.setState({ resetActive: true });
        else if (_.isEqual(transform, d3_xyzoom.xyzoomIdentity) && this.state.resetActive !== false) this.setState({ resetActive: false });
      }
    }

    componentDidMount() {
      this.drawGraph();
      if (this.props.multiBrush && this.props.index) {
        this.props.storeSpectrumRefs(this.props.index, this);
      }
    }

    drawGraph() {
      // init graph
      this.initializeGraph();

      // add defs section
      this.drawDefs();

      // draw axis and ticks and datalines
      this.drawAxis();

      // draw inline title
      this.drawTitle();

      // draw left side label
      this.drawYlabel();

      this.drawXlabel();

      if (this.props.type === 'waveform' && this.refNode.drawGraph) {
        this.refNode.drawGraph(this);
      }

      // draw datalines
      this.drawDataLines();

      if (this.props.type !== 'waveform' && this.refNode.drawGraph) {
        this.refNode.drawGraph(this);
      }

      // draw a selection line
      this.drawSelection();

      this.drawPoints();

      this.addMouseoverEventListener();

      this.addClickEventListener();

      this.addDoubleClickEventListener();

      this.redrawDataLines(this.x, this.y, this); // TODO: utils context chart requires redrawdatalines for some reason
    }

    setNewTransform = (selection) => {
      const oldTransform = d3_xyzoom.xyzoomTransform(this.chart);
      const kx = this.container.getBoundingClientRect().width / (selection[1] - selection[0]);
      const t = d3_xyzoom.xyzoomIdentity
        .translate(-selection[0] * kx, oldTransform.y)
        .scale(kx, oldTransform.ky);

      this.setState({ transformX: -selection[0] * kx, kx }, () => {
        if (this.zoom) d3.select(this.chart).call(this.zoom.transform, t);
      });
    };

    zoomHandler() {
      // eslint-disable-next-line no-underscore-dangle
      if (this._isTransforming) return; // Skip if we're already transforming
      if (d3.event.sourceEvent && d3.event.sourceEvent.type === 'brush') return;

      const { x, y } = this.state.axisLocked;
      if (!this.chart) return;

      const transform = d3_xyzoom.xyzoomTransform(this.chart);
      const new_xScale = transform.rescaleX(this.x2);
      const new_yScale = transform.rescaleY(this.y2);

      const eventTransform = d3.event.transform;
      const xRange = this.x.range().map(transform.invertX, eventTransform);
      let brushRange = [];

      if (xRange[0] < this.x.range()[0] || xRange[1] > this.x.range()[1]) {
        if (xRange[0] < this.x.range()[0] && xRange[1] > this.x.range()[1]) {
          brushRange = [0, this.x.range()[1]];
        } else if (xRange[0] < this.x.range()[0] && xRange[1] <= this.x.range()[1]) {
          brushRange = [this.x.range()[0], xRange[1]];
        } else if (xRange[0] >= this.x.range()[0] || xRange[1] > this.x.range()[1]) {
          brushRange = [xRange[0], this.x.range()[1]];
        }
      } else {
        brushRange = xRange;
      }

      if (!y) {
        this.setState({
          yScaleDomain: new_yScale.domain(),
          yAxisUpperLimit: this.formatAxisLimitNumber(new_yScale.domain()[1]),
          transformY: transform.y,
          ky: transform.ky
        });
        if (this.props.storeTransform) {
          this.props.storeTransform({ transformY: transform.y, ky: transform.ky });
        }
      }

      if (!x) {
        this.setState({ transformX: transform.x, kx: transform.kx });
        if (this.props.storeTransform) {
          this.props.storeTransform({ transformX: transform.x, kx: transform.kx });
          this.props.brushCb(brushRange);
        }
      }

      if (transform.kx === 1 && transform.ky < 1) {
        if (d3.event.sourceEvent && d3.event.sourceEvent.wheelDelta > 0) {
          const t = d3_xyzoom.xyzoomIdentity
            .translate(transform.x, transform.y)
            .scale(1.000001, transform.ky);
          if (this.zoom) d3.select(this.chart).call(this.zoom.transform, t);
        }
      }

      if (!y) this.y.domain(new_yScale.domain());
      if (!x) this.x.domain(new_xScale.domain());

      if (this.props.multiBrush && this.props.redrawAllSpectrums) {
        this.props.redrawAllSpectrums();
      } else {
        this.redraw();
      }

      if (!x && this.lineContext && this.lineContext.refNode.zoomHandler) {
        this.lineContext.refNode.zoomHandler(this);
      }
      if (!x && this.props.type === 'baseline') {
        if (this.refNode.zoomHandlerBaseline) this.refNode.zoomHandlerBaseline(this);
      }
    }

    // hack to restore transform x, y to correct values after panning when one of the axis is locked
    onZoomEnd = () => {
      if (d3.event.sourceEvent && d3.event.sourceEvent.type === 'brush') return;
      const { transformX, transformY, kx, ky } = this.state;

      if (!this.chart) return;
      const oldTransform = d3_xyzoom.xyzoomTransform(this.chart);

      // Only apply transform if there's a significant difference to avoid infinite recursion
      const epsilon = 0.000001; // Small threshold for floating point comparison
      const hasSignificantChange =
        Math.abs(oldTransform.x - transformX) > epsilon ||
        Math.abs(oldTransform.y - transformY) > epsilon ||
        Math.abs(oldTransform.kx - kx) > epsilon ||
        Math.abs(oldTransform.ky - ky) > epsilon;

      if (this.zoom && hasSignificantChange) {
        const t = d3_xyzoom.xyzoomIdentity
          .translate(transformX, transformY)
          .scale(kx, ky);

        // Set a flag to prevent recursive calls
        // eslint-disable-next-line no-underscore-dangle
        this._isTransforming = true;
        d3.select(this.chart).call(this.zoom.transform, t);
        // eslint-disable-next-line no-underscore-dangle
        this._isTransforming = false;
      }
    }

    changeZoomableAxis = (type = 'BOTH') => {
      if (type === 'X') this.zoom.scaleRatio([1, 0]);
      if (type === 'Y') this.zoom.scaleRatio([0, 1]);
      if (type === 'BOTH') this.zoom.scaleRatio([1, 1]);
      if (type === 'NONE') this.zoom.scaleRatio([0, 0]);

      d3.select(this.chart).call(this.zoom);
      if (NoChartDblClickZoom.includes(this.props.type)) d3.select(this.chart).on('dblclick.zoom', null);
    };

    /**
     * Context (navigator) charts use .noClipDataline without a clip-path, and stroke extends past
     * the plot edges. Inset x/y ranges keep the line inside the SVG. Parent Chart also sets
     * svg { overflow: visible }, so LineChartContainer forces overflow:hidden for *-ctx charts.
     */
    getContextPlotRanges(width, height) {
      if (this.props.type !== 'context') return null;
      const w = width;
      const h = height;
      if (!Number.isFinite(w) || !Number.isFinite(h) || w < 8 || h < 8) return null;

      const strokePad = 3;
      const topPad = Math.min(14, Math.max(strokePad, Math.floor(h * 0.28)));
      const bottomPad = Math.min(10, Math.max(strokePad, Math.floor(h * 0.22)));
      const yBottom = Math.max(topPad + 2, h - bottomPad);
      const xPad = Math.min(4, Math.max(1, w * 0.008));
      const x1 = xPad;
      const x2 = Math.max(x1 + 2, w - xPad);

      return { yRange: [yBottom, topPad], xRange: [x1, x2] };
    }

    initializeGraph() {
      const rect = this.container.getBoundingClientRect();
      const contextRanges = this.getContextPlotRanges(rect.width, rect.height);
      const yRange = contextRanges ? contextRanges.yRange : [rect.height, 0];
      const xRange = contextRanges ? contextRanges.xRange : [0, rect.width];

      this.y = d3.scaleLinear().range(yRange);
      this.y2 = d3.scaleLinear().range(yRange);
      this.y.domain(this.props.range);
      this.y2.domain(this.props.range);
      if (this.props.xAxis === 'linear') {
        this.x = d3.scaleLinear();
        this.x2 = d3.scaleLinear();
      } else {
        this.x = d3.scaleTime();
        this.x2 = d3.scaleTime();
      }
      if (this.props.domain === undefined) {
        // select largest timescale from datasets that we got
        const domain = [];

        this.props.data.forEach((data) => {
          const dataDomain = d3.extent(data, d => d.x);
          if ((dataDomain[0] !== undefined && dataDomain[0] < domain[0]) || !domain[0]) domain[
            0] = dataDomain[0];
          if ((dataDomain[1] !== undefined && dataDomain[1] > domain[1]) || !domain[1]) domain[
            1] = dataDomain[1];
        });
        this.x.range(xRange).domain(domain);
        this.x2.range(xRange).domain(domain);
      } else {
        this.x.range(xRange).domain(this.props.domain);
        this.x2.range(xRange).domain(this.props.domain);
      }

      this.xAxis = d3.axisBottom(this.x);
      this.yAxis = d3.axisLeft(this.y);

      this.initilizeZoom();

      d3.select(this.chart)
        .attr('width', rect.width)
        .attr('height', rect.height);

      if (!NoChartZoomTypes.includes(this.props.type)) {
        if ((this.props.contextChart || this.props.enableZoom) && !this.zoomApplied) {
          this.enableZoom();
        } else if (!(this.props.contextChart || this.props.enableZoom) && this.zoomApplied) {
          this.disableZoom();
        }
      }
    }

    drawDefs() {
      const { chartName } = this.props;

      const chartSel = d3.select(this.chart);
      const existingClipRect = chartSel.select(`#${chartName}-clip > rect`);
      const w = this.chart.getBoundingClientRect().width;
      const h = this.chart.getBoundingClientRect().height;

      if (!existingClipRect.empty()) {
        this.clipPath = existingClipRect;
        existingClipRect.attr('x', 0).attr('y', 0).attr('width', w).attr('height', h);
        chartSel.select(`defs.${chartName} linearGradient`).remove();
        if (this.props.applyGradient) defsUtils.addLinearGradient(this.chart, this.y, chartName, this.props.assetConditionLevels);
        if (this.refNode.drawDefs) this.refNode.drawDefs();
        return;
      }

      chartSel.select(`defs.${chartName}`).remove();
      this.defs = defsUtils.createDefs(this.chart, chartName);
      this.clipPath = defsUtils.addClipPath(this.chart, chartName);

      if (this.props.applyGradient) defsUtils.addLinearGradient(this.chart, this.y, chartName, this.props.assetConditionLevels);

      if (this.refNode.drawDefs) this.refNode.drawDefs();
    }

    drawAxis() {
      const { chartName, xAxisTicks } = this.props;
      d3.selectAll(`.${chartName}.axis`).remove();
      this.yAxisGridlines = gridUtils.yAxisGridlines(this.chart, this.y, 2, `${chartName}`);
      this.xAxisGridlines = gridUtils.xAxisGridlines(this.chart, this.x, xAxisTicks, `${chartName}`);
      this.ticksBottom = gridUtils.ticksBottom(this.chart, this.x, xAxisTicks, `${chartName}`);
      this.ticksLeft = gridUtils.ticksLeft(this.chart, this.y, 2, `${chartName}`);
      this.ticksLeft.on('click', null);
      if (this.refNode.drawAxis) this.refNode.drawAxis(this);
    }

    changeYAxisDomain() {
      const currentUpper = this.getCurrentYAxisUpperLimit();
      this.setState((prevState) => {
        const nextUpperText = Number.isFinite(currentUpper)
          ? this.formatAxisLimitNumber(currentUpper)
          : prevState.yAxisUpperLimit;
        const { yAxisLimitMode, yAxisPercent } = prevState;

        return {
          yAxisModalOpen: true,
          yAxisUpperLimit: nextUpperText,
          yAxisError: '',
          yAxisModalSnapshot: {
            mode: yAxisLimitMode,
            yAxisUpperLimit: nextUpperText,
            yAxisPercent
          }
        };
      });
    }

    redraw(x = this.x, y = this.y) {
      if (!this.container) return;
      const { xAxisTicks } = this.props;
      const rect = this.container.getBoundingClientRect();
      const contextRanges = this.getContextPlotRanges(rect.width, rect.height);
      if (contextRanges) {
        this.y.range(contextRanges.yRange);
        this.y2.range(contextRanges.yRange);
        this.x.range(contextRanges.xRange);
        this.x2.range(contextRanges.xRange);
      } else {
        this.y.range([rect.height, 0]);
        this.x.range([0, rect.width]);
      }

      d3.select(this.chart)
        .attr('width', rect.width)
        .attr('height', rect.height);

      const clipW = rect.width;
      const clipH = rect.height;
      d3.select(this.chart).select(`#${this.props.chartName}-clip > rect`)
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', clipW)
        .attr('height', clipH);

      // move axises
      const plotW = rect.width;
      const plotH = rect.height;

      this.yAxisGridlines
        .call(d3.axisRight(y)
          .ticks(2)
          .tickSize(plotW)
          .tickFormat(''));

      this.xAxisGridlines
        .call(d3.axisTop(x)
          .ticks(xAxisTicks)
          .tickSize(plotH)
          .tickFormat(''));

      this.ticksLeft
        .call(d3.axisLeft(y)
          .ticks(2)
          .tickSize(10));

      // Redraw bottom ticks and localize time-based axes.
      let axis = d3.axisBottom(x)
        .ticks(xAxisTicks)
        .tickSize(10);

      const sampleTicks = typeof x.ticks === 'function' ? x.ticks(1) : [];
      if (sampleTicks.length && sampleTicks[0] instanceof Date) {
        axis = axis.tickFormat(gridUtils.formatTimeTick);
      }

      this.ticksBottom.call(axis);

      this.drawSelection(x, y);
      this.drawPoints(x, y);
      this.redrawDataLines(x, y, this);

      // hack to make zoom work on ML tab as drawGraph is not being called when expanded unlike in charts tab.
      if (!NoChartZoomTypes.includes(this.props.type)) {
        if ((this.props.contextChart || this.props.enableZoom) && !this.zoomApplied) {
          this.enableZoom();
        } else if (!(this.props.contextChart || this.props.enableZoom) && this.zoomApplied) {
          this.disableZoom();
        }
      }
      if (this.refNode.redraw) this.refNode.redraw(this);
    }

    initilizeZoom = () => {
      this.zoom = d3_xyzoom.xyzoom()
        .extent([
          [0, 0],
          [this.container.getBoundingClientRect().width,
            this.container.getBoundingClientRect().height]])
        .scaleExtent([[1, 500], [0.1, 10]])
        .translateExtent([
          [0, -(this.container.getBoundingClientRect().height * 9)],
          [this.container.getBoundingClientRect().width,
            this.container.getBoundingClientRect().height]])
        .on('end', this.onZoomEnd)
        .on('zoom', this.zoomHandler);

      // waveform should zoom around center point
      if (this.props.type === 'waveform') {
        this.zoom.translateExtent([
          [0, -(this.container.getBoundingClientRect().height * 5)],
          [this.container.getBoundingClientRect().width,
            this.container.getBoundingClientRect().height * 5]]);
      }

      this.zoom.scaleRatio([1, 1]);
    };

    enableZoom = () => {
      this.zoomApplied = true;
      d3.select(this.chart)
        .call(this.zoom);
      if (NoChartDblClickZoom.includes(this.props.type)) d3.select(this.chart).on('dblclick.zoom', null);
      const maxYdomain = ((this.y2.domain()[1] - this.y2.domain()[0]) / 0.1) + this.y2.domain()[0];
      // Global minimum allowed upper limit (Merck request): allow low-amplitude/low-speed scaling everywhere.
      const minYdomain = 0.002;
      const sessionSettings = readChartSettingsFromSession();
      const mode = (sessionSettings && sessionSettings[Y_AXIS_SETTINGS_KEYS.mode]) || this.state.yAxisLimitMode || 'absolute';
      const savedUpperLimit = sessionSettings && sessionSettings[Y_AXIS_SETTINGS_KEYS.upperLimit];
      const yAxisUpperLimit = (mode === 'absolute' && savedUpperLimit != null && savedUpperLimit !== '')
        ? savedUpperLimit
        : this.formatAxisLimitNumber(this.y2.domain()[1]);
      this.setState({
        maxYdomain,
        minYdomain,
        yAxisUpperLimit
      }, () => {
        if (this.yAxisApplyScheduledFromDataChange) return;
        this.applyPreferredPercentYAxisIfNeeded();
        this.applyPreferredAbsoluteYAxisIfNeeded();
      });
    };

    disableZoom = () => {
      if (this.chart && this.zoom) {
        d3.select(this.chart).transition()
          .duration(500)
          .call(this.zoom.transform, d3_xyzoom.xyzoomIdentity)
          .on('end', () => {
            if (!this.state.axisLocked.x && this.lineContext && this.lineContext.refNode) {
              this.lineContext.refNode.resetBrushSelection();
            }
          });
      }
      this.zoomApplied = false;
      d3.select(this.chart).on('.zoom', null);
      this.setState({
        resetActive: false
      });
    };

    drawSelectionLines(x = this.x, y = this.y) {
      if (this.props.selection) {
        const dataPoint = this.props.selection;
        d3.select(`.${this.props.chartName}.selection.line`)
          .attr('x1', x(dataPoint.x))
          .attr('x2', x(dataPoint.x));

        d3.select(`.${this.props.chartName}.selection.circle`)
          .attr('cx', x(dataPoint.x))
          .attr('cy', y(dataPoint.y));
      }

      // move points circle and line
      if (!_.isEmpty(this.props.points)) {
        const dataPoint = this.props.points;
        d3.select(`.${this.props.chartName}.pointSelection.line`)
          .attr('x1', x(dataPoint.x))
          .attr('x2', x(dataPoint.x));

        d3.select(`.${this.props.chartName}.pointSelection.circle`)
          .attr('cx', x(dataPoint.x))
          .attr('cy', y(dataPoint.y));
      }
      if (this.refNode.redraw) this.refNode.redraw(this);
    }

    drawTitle() {
      d3.selectAll(`.${this.props.chartName}.title`).remove();
      this.chartTitle = graphUtils.drawTitle(this.chart, this.props.title, this.props.chartName);
    }

    drawXlabel() {
      d3.selectAll(`.${this.props.chartName}.xlabel`).remove();
      if (this.props.noXlabel) return;
      const label = getXAxisLabel(this.props);
      if (label && !this.props.miniMode) graphUtils.drawXAxisLabel(this.chart, label, this.props.chartName);
    }

    drawYlabel() {
      d3.selectAll(`.${this.props.chartName}.ylabel`).remove();
      let label = this.props.ylabel;
      if (this.props.chart_size !== 'Large' && label && label.length >= 8 && ((this.props.contextChart && !this.props.miniMode) || this.props.showZoomLocks === 'y')) {
        label = label.slice(0, 9);
        label = `${label}..`;
        const noOfSpaces = this.props.ylabel.length - 4;
        for (let i = 0; i < noOfSpaces; i++) {
          label += '\xa0';
        }
      }
      if (this.props.chart_size !== 'Large' && this.props.ylabel && this.props.ylabel.length <= 10 && this.props.ylabel.length >= 7 && ((this.props.contextChart && !this.props.miniMode) || this.props.showZoomLocks === 'y')) {
        for (let i = 0; i < 6; i++) {
          label += '\xa0';
        }
      }
      this.chartLabel = graphUtils.drawYAxisLabel(this.chart, label, this.props.chartName, this.props.ylabel);
      if (this.refNode.toggleYaxisUnits && this.refNode.state.allowYUnitToggle) {
        this.chartLabel = this.chartLabel
          .attr('cursor', 'pointer')
          .attr('font-weight', '300')
          .attr('stroke', this.props.theme.primaryColor)
          .on('click', () => {
            d3.event.stopPropagation();
            this.refNode.toggleYaxisUnits();
          });
      }
    }

    drawSelection(x = this.x, y = this.y) {
      // draw a selection line
      let dataPoint = this.props.selection;
      if (dataPoint && _.isArray(dataPoint)) {
        dataPoint = dataPoint.filter(item => this.props.activeData[item.dataIdx]);
      }
      if (dataPoint && !_.isEmpty(dataPoint)) {
        if (this.refNode.drawSelection) this.refNode.drawSelection(this, dataPoint);

        else {
          d3.selectAll(`.${this.props.chartName}.selection`).remove();
          if (!_.isEmpty(dataPoint)) {
            graphUtils.drawLineOnPos(this.chart, x(dataPoint.x), 0, x(dataPoint.x), this.container.getBoundingClientRect().height, `${this.props.chartName} selection`)
              .attr('pointer-events', 'none');
            graphUtils.drawCircleOnPos(this.chart, x(dataPoint.x), y(dataPoint.y), 3, `${this.props.chartName} selection`)
              .attr('pointer-events', 'none');
          }
        }
      }
    }

    getLineGenerator(x = this.x, y = this.y) {
      return d3.line()
        .curve(d3.curveLinear)
        .x(d => x(d.x))
        .y(d => y(d.y));
    }

    drawDataLines(x = this.x, y = this.y) {
      const { colors, hoveredLegendItem, activeLegendItem } = this.props;
      if (this.refNode.drawDataLines) {
        this.refNode.drawDataLines(this);
      }
      if (this.refNode.drawDataLinesOverride) {
        this.refNode.drawDataLinesOverride(this);
        return;
      }
      const { chartName } = this.props;
      d3.selectAll(`.${chartName}.dataline`).remove();
      this.chartDatalines = [];

      // Ensure this.props.data is valid for rendering
      const validData = Array.isArray(this.props.data) ? this.props.data : [];

      validData.forEach((dataSet, idx) => {
        // Skip if dataSet is not a valid array or is empty
        if (!Array.isArray(dataSet) || dataSet.length === 0) return;

        const dataline = graphUtils.drawDataline(
          this.chart,
          dataSet,
          x,
          y,
          `${chartName} dataline idx-${idx}`,
          { lineFunc: this.getLineGenerator(x, y) }
        );
        dataline.attr('stroke', colors ? colors[idx] : '#91B846');

        // Apply opacity and styling using extracted common logic
        const { opacity, strokeWidth } = this.getLineOpacityAndStyle(idx, activeLegendItem, hoveredLegendItem);
        dataline.attr('opacity', opacity);
        dataline.attr('stroke-width', strokeWidth);

        this.chartDatalines.push(dataline);
      });
    }

    drawPoints(x = this.x, y = this.y) {
      d3.selectAll(`.${this.props.chartName}.pointSelection`).remove();
      if (this.props.points) {
        if (this.refNode.drawPoints) this.refNode.drawPoints(this, this.props.points);
        else {
          // default functionality
          const dataPoint = this.props.waveAndSpecSelection || this.props.points;
          if (_.isEmpty(dataPoint)) return;
          d3.selectAll(`.${this.props.chartName}.pointSelection`).remove();
          graphUtils.drawLineOnPos(this.chart, x(dataPoint.x), 0, x(dataPoint.x), this.container.getBoundingClientRect().height, `${this.props.chartName} pointSelection`);
          graphUtils.drawCircleOnPos(this.chart, x(dataPoint.x), y(dataPoint.y), 4, `${this.props.chartName} pointSelection`);
          if (this.props.setDataPosAndDataPoint) {
            let dataPos = {
              x: false,
              y: false
            };
            if (x(dataPoint.x) >= 0 && x(dataPoint.x) <= this.container.getBoundingClientRect().width
              && y(dataPoint.y) >= 0 && y(dataPoint.y) <= parseInt(this.props.height, 10)) {
              dataPos = {
                x: x(dataPoint.x),
                y: y(dataPoint.y)
              };
              if (dataPos.x > this.container.getBoundingClientRect().width - 250) dataPos.alignLeft = true;
            }
            this.props.setDataPosAndDataPoint(dataPos, dataPoint);
          }
        }
      }
    }

    addMouseoverEventListener() {
      if (this.props.mouseover) {
        // TODO: event listener is not removed anywhere
        if (this.refNode.addMouseoverEventListener) this.refNode.addMouseoverEventListener(this);
        else {
          if (this.mouseover) return;
          // this is really slow on linechart
          this.mouseover = graphUtils.addMouseoverEvent(this.chart, () => {
            const dataPoint = helpers.closestExactDataPoint(this);
            if (!dataPoint) return;
            this.props.mouseover(dataPoint, this.props.chartName);
          });
        }
      }
    }

    addClickEventListener() {
      this.mouseClick = null;
      if (!this.mouseClick && this.props.mouseClick) {
        this.mouseClick = graphUtils.addMouseClickEvent(this.chart, (event) => {
          const dataPoint = helpers.closestExactDataPoint(this);
          if (!dataPoint) return;
          this.props.mouseClick(dataPoint, event);
        });
      }
    }

    addDoubleClickEventListener() {
      this.doubleClick = null;
      if (!this.doubleClick && this.props.doubleClick) {
        this.doubleClick = graphUtils.addDoubleClickEvent(this.chart, () => {
          this.props.doubleClick();
        });
      }
    }

    redrawDataLines(x = this.x, y = this.y) {
      if (this.refNode && this.refNode.redrawDataLines) this.refNode.redrawDataLines(x, y, this);
      else if (this.props.parent && this.props.parent.refNode && this.props.parent.refNode.redrawDataLines) this.props.parent.refNode.redrawDataLines(x, y, this);
      else {
        const { hoveredLegendItem, activeLegendItem } = this.props;
        this.props.data.forEach((dataSet, idx) => {
          if (!_.isEmpty(this.chartDatalines[idx])) {
            const dataline = this.chartDatalines[idx]
              .attr('d', this.getLineGenerator(x, y));
            dataline.attr('stroke', this.props.colors ? this.props.colors[idx] : '#91B846');

            // Apply opacity and styling using extracted common logic
            const { opacity, strokeWidth } = this.getLineOpacityAndStyle(idx, activeLegendItem, hoveredLegendItem);
            dataline.attr('opacity', opacity);
            dataline.attr('stroke-width', strokeWidth);
          }
        });
      }
    }

    setRef(node) {
      // if ref callback gets called with null it is unmounting or in preliminary stages so remove eventlistener
      // when it has props the node exists and we can set the ref and eventlistener
      if (!node) {
        window.removeEventListener('resize', () => this.redraw(), false);
      } else if (node.props) {
        window.addEventListener('resize', () => this.redraw(), false);
        this.refNode = node;
      }
    }

    increaseYAxis = () => {
      if (this.refNode && this.refNode.increaseYAxis) this.refNode.increaseYAxis();
    };

    decreaseYAxis = () => {
      if (this.refNode && this.refNode.decreaseYAxis) this.refNode.decreaseYAxis();
    };

    toggleBools = (axis) => {
      this.setState((prevState) => {
        const nextAxisLocked = { ...prevState.axisLocked, [axis]: !prevState.axisLocked[axis] };
        if (this.props.toggleZoomLocks) this.props.toggleZoomLocks({ [axis]: nextAxisLocked[axis] });
        return { axisLocked: nextAxisLocked };
      }, this.zoomAxis);
    }

    resetChartZoom = (type) => {
      if (type === 'x') {
        const { transformY, ky } = this.state;
        const t = d3_xyzoom.xyzoomIdentity
          .translate(0, transformY)
          .scale(1, ky);
        d3.select(this.chart).transition()
          .duration(500)
          .call(this.zoom.transform, t)
          .on('end', () => {
            if (!this.state.axisLocked.x && this.lineContext.refNode) {
              this.lineContext.refNode.resetBrushSelection();
            }
          });
        if (this.props.storeTransform) {
          this.props.storeTransform({ kx: 1, transformX: 0 });
        }
      } else {
        d3.select(this.chart).transition()
          .duration(500)
          .call(this.zoom.transform, d3_xyzoom.xyzoomIdentity)
          .on('end', () => {
            if (!this.state.axisLocked.x && this.lineContext && this.lineContext.refNode) {
              this.lineContext.refNode.resetBrushSelection();
            }
          });
        if (this.props.storeTransform) {
          this.props.storeTransform({ transformY: 0, ky: 1, kx: 1, transformX: 0 });
        }
      }
    };

    zoomAxis = () => {
      const { axisLocked } = this.state;
      const brush = d3.select(`.${this.props.chartName}-ctx.brush`);
      const brushSelection = d3.brushSelection(brush) && d3.brushSelection(brush).node();
      if (!brushSelection) {
        if (!axisLocked.x && !axisLocked.y) this.changeZoomableAxis('BOTH');
        else if (!axisLocked.x) this.changeZoomableAxis('X');
        else if (!axisLocked.y) this.changeZoomableAxis('Y');
        else this.changeZoomableAxis('NONE');
        return;
      }

      if (!axisLocked.x) {
        d3.selectAll(`.${this.props.chartName}-ctx.brush>.overlay`)
          .attr('pointer-events', 'all')
          .attr('cursor', 'crosshair');
        if (brushSelection) {
          d3.selectAll(`.${this.props.chartName}-ctx.brush>.handle`).style('display', 'block');
        }
        if (!axisLocked.y) this.changeZoomableAxis('BOTH');
        else this.changeZoomableAxis('X');
        return;
      }
      d3.selectAll(`.${this.props.chartName}-ctx.brush>.overlay`)
        .attr('pointer-events', 'none')
        .attr('cursor', 'not-allowed');
      d3.selectAll(`.${this.props.chartName}-ctx.brush>.handle`).style('display', 'none');
      if (!axisLocked.y) {
        this.changeZoomableAxis('Y');
        return;
      }
      this.changeZoomableAxis('NONE');
    };

    onChangeInput = (e) => {
      const { maxYdomain, minYdomain } = this.state;
      this.setState({
        yAxisUpperLimit: e.target.value
      });
      if (Number.parseFloat(e.target.value)) {
        const value = Number.parseFloat(e.target.value);
        if (this.isValueOutsideYAxisBounds(value)) {
          this.setState({
            yAxisError: `Please enter a value between ${this.formatAxisLimitNumber(minYdomain)} and ${this.formatAxisLimitNumber(maxYdomain)}`
          });
          return;
        }
        this.setState({
          yAxisError: ''
        });
      } else {
        this.setState({
          yAxisError: 'Not a valid number'
        });
      }
      if (e.target.value === '') {
        this.setState({
          yAxisError: ''
        });
      }
    }

    onChangePercentInput = (e) => {
      this.setState({ yAxisPercent: e.target.value });
      mergeChartSettingsIntoSession({ [Y_AXIS_SETTINGS_KEYS.percent]: e.target.value });
      const raw = e.target.value;
      if (raw === '') {
        this.setState({ yAxisError: '' });
        return;
      }
      const pct = Number.parseFloat(raw);
      if (!Number.isFinite(pct) || pct <= 0) {
        this.setState({ yAxisError: 'Please enter a valid percent (> 0)' });
        return;
      }
      const peak = this.getGlobalPeakY();
      if (!Number.isFinite(peak) || peak === null) {
        this.setState({ yAxisError: 'Peak is unavailable (no data)' });
        return;
      }
      const candidate = peak * (pct / 100);
      const { maxYdomain } = this.state;
      if (!Number.isFinite(candidate)) {
        this.setState({ yAxisError: 'Computed limit is not valid' });
        return;
      }
      // Percent mode intentionally ignores the global minimum bound (minYdomain).
      // Only enforce the max bound to avoid extreme/invalid scaling.
      if (this.isValueOutsideYAxisBounds(candidate, { enforceMin: false })) {
        this.setState({
          yAxisError: `Computed limit must be ≤ ${this.formatAxisLimitNumber(maxYdomain)}`
        });
        return;
      }
      this.setState({ yAxisError: '' });
    }

    changeYAxisScale = (value) => {
      if (!this.shouldApplyYAxisLimitToChart()) return;
      // Refs can be null when deferred callbacks run after unmount or before attach.
      if (!this.container || !this.chart) return;
      const oldDomain = this.y2.domain();

      // 0 should be in the initial location for all charts
      const newLowerDomain = (oldDomain[0] / oldDomain[1]) * value;
      this.y.domain([newLowerDomain, value]);
      const ky = (this.y2.domain()[1] - this.y2.domain()[0]) / (value - newLowerDomain);

      const height = this.container.getBoundingClientRect().height;

      // will work where y axis is linear scale (IMPORTANT).
      const perpixel = height / (value - newLowerDomain);
      const oldTransform = d3_xyzoom.xyzoomTransform(this.chart);

      const transformY = (value - this.y2.domain()[1]) * perpixel;
      const t = d3_xyzoom.xyzoomIdentity
        .translate(oldTransform.x, transformY)
        .scale(oldTransform.kx, ky);

      const nextState = {
        yScaleDomain: [newLowerDomain, value],
        yAxisUpperLimit: this.formatAxisLimitNumber(value),
        transformY,
        ky
      };
      if (this.props.storeTransform) {
        this.setState(nextState);
        this.props.storeTransform({ transformY, ky });
        return;
      }
      if (this.zoom) {
        this.setState(nextState, () => d3.select(this.chart).call(this.zoom.transform, t));
      }
    };

    onSaveYAxislimit = () => {
      const { yAxisUpperLimit, yAxisError, yAxisLimitMode, yAxisPercent } = this.state;
      if (yAxisError) return;

      if (yAxisLimitMode === 'percent') {
        const pct = Number.parseFloat(yAxisPercent);
        if (!Number.isFinite(pct) || pct <= 0) return;
        const peak = this.getGlobalPeakY();
        if (!Number.isFinite(peak) || peak === null) return;
        const candidate = peak * (pct / 100);
        if (!Number.isFinite(candidate)) return;
        this.changeYAxisScale(candidate);
        mergeChartSettingsIntoSession({
          [Y_AXIS_SETTINGS_KEYS.mode]: 'percent',
          [Y_AXIS_SETTINGS_KEYS.percent]: yAxisPercent
        });
        this.setState({ yAxisModalOpen: false, yAxisModalSnapshot: null });
        return;
      }

      if (Number.parseFloat(yAxisUpperLimit)) {
        const value = Number.parseFloat(yAxisUpperLimit);
        this.changeYAxisScale(value);
        mergeChartSettingsIntoSession({
          [Y_AXIS_SETTINGS_KEYS.mode]: 'absolute',
          [Y_AXIS_SETTINGS_KEYS.upperLimit]: yAxisUpperLimit
        });
        this.setState({ yAxisModalOpen: false, yAxisModalSnapshot: null });
      }
    }

    handleEnter = (e) => {
      if (e.key === 'Enter') {
        this.onSaveYAxislimit();
      }
    }

    onClickReset = () => {
      if (this.props.resetChartsZoom) {
        this.props.resetChartsZoom();
      } else this.resetChartZoom();
    };

    // Extract common logic for line opacity and styling to eliminate code duplication
    getLineOpacityAndStyle = (idx, activeLegendItem, hoveredLegendItem) => {
      const isHighlighted = activeLegendItem === idx || hoveredLegendItem === idx;
      const shouldApplyLegendOpacity = this.props.showOverlay || this.props.enableLegendInteraction;
      const hasActiveOrHoveredItem = shouldApplyLegendOpacity && (activeLegendItem !== null || hoveredLegendItem !== null);

      if (!this.props.activeData[idx]) {
        return { opacity: 0, strokeWidth: 1.5 };
      }

      if (hasActiveOrHoveredItem) {
        if (isHighlighted) {
          // Highlight the active/hovered line
          return { opacity: 1, strokeWidth: 2.5 };
        }
        // De-emphasize other lines with consistent opacity value
        return { opacity: 0.25, strokeWidth: 1.5 };
      }

      // Normal state when no legend item is active or hovered
      return { opacity: 1, strokeWidth: 1.5 };
    };

    render() {
      const visiblePeakY = this.state.yAxisModalOpen ? this.getGlobalPeakY() : null;
      const percentAsNumber = Number.parseFloat(this.state.yAxisPercent);
      const computedYMaxFromPercent = (this.state.yAxisLimitMode === 'percent'
        && Number.isFinite(percentAsNumber)
        && percentAsNumber > 0
        && Number.isFinite(visiblePeakY))
        ? (visiblePeakY * (percentAsNumber / 100))
        : null;

      return (
        <Fragment>
          <div style={{ position: 'relative' }}>
            <LineChartContainer
              innerRef={(node) => { this.container = node; }}
              chartName={this.props.chartName}
              applyGradient={this.props.applyGradient || (typeof this.props.filterMinVal === 'number' && typeof this.props.filterMaxVal === 'number') || this.props.enableOnOffIndicator}
              height={this.props.height}
              top={this.props.type === 'overallAlarm'}
              noMarginBottom={this.props.type === 'context' || !this.props.contextChart}
              xAxisLocked={this.state.axisLocked.x}
              miniMode={this.props.miniMode}
              hierarchyViewPane={this.props.hierarchyViewPane}
            >
              {!_.isEmpty(this.props.data) && !NoChartZoomTypes.includes(this.props.type) && this.state.resetActive && (
                <SvgContainer absolute right="-0px" onClick={this.onClickReset}>
                  <ResetSvg />
                </SvgContainer>
              )}
              {this.state.yAxisModalOpen && (
                <YAxisLimitModal
                  mode={this.state.yAxisLimitMode}
                  yAxisUpperLimit={this.state.yAxisUpperLimit}
                  yAxisPercent={this.state.yAxisPercent}
                  visiblePeakY={visiblePeakY}
                  computedYMaxFromPercent={computedYMaxFromPercent}
                  maxYdomain={this.state.maxYdomain}
                  minYdomain={this.state.minYdomain}
                  error={this.state.yAxisError}
                  formatAxisLimitNumber={this.formatAxisLimitNumber}
                  onClose={() => this.setState({ yAxisModalOpen: false, yAxisModalSnapshot: null })}
                  onModeChange={this.setYAxisLimitMode}
                  onAbsoluteChange={this.onChangeInput}
                  onPercentChange={this.onChangePercentInput}
                  onSave={this.onSaveYAxislimit}
                  onKeyPress={this.handleEnter}
                  isSaveDisabled={(() => {
                    const snap = this.state.yAxisModalSnapshot;
                    const hasChanged = snap && (
                      this.state.yAxisLimitMode !== snap.mode ||
                      this.state.yAxisUpperLimit !== snap.yAxisUpperLimit ||
                      this.state.yAxisPercent !== snap.yAxisPercent
                    );
                    const invalid = this.state.yAxisError !== '' ||
                      (this.state.yAxisLimitMode === 'absolute'
                        ? (this.state.yAxisUpperLimit === '')
                        : (this.state.yAxisPercent === '' || !Number.isFinite(Number.parseFloat(this.state.yAxisPercent)) || Number.parseFloat(this.state.yAxisPercent) <= 0));
                    return !hasChanged || invalid;
                  })()}
                />
              )}
              {this.props.type === 'overallAlarm' && (
                <ButtonContainer>
                  <Button onClick={this.increaseYAxis}>
                    <Plus color="#9ea09a" />
                  </Button>
                  <Button onClick={this.decreaseYAxis} disabled={this.state.decreaseDisabled}>
                    <Minus color="#9ea09a" />
                  </Button>
                </ButtonContainer>
              )}
              <svg ref={(node) => { this.chart = node; }} />
              <WrappedComponent {...this.props} ref={this.setRef} />

              {(((!_.isEmpty(this.props.data) && this.props.contextChart === true && this.props.type !== 'overallAlarm' && this.props.type !== 'utilization') && !this.props.miniMode) || this.props.showZoomLocks) && (
                <Fragment>
                  {(!this.props.showZoomLocks || (this.props.showZoomLocks && (this.props.showZoomLocks === 'x' || this.props.showZoomLocks === 'xy'))) && (
                    <ButtonX onClick={() => this.toggleBools('x')}>
                      {this.state.axisLocked.x ? <LockedZoomSVG /> : <UnlockedZoomSVG />}
                    </ButtonX>
                  )}
                  {(!this.props.showZoomLocks || (this.props.showZoomLocks && (this.props.showZoomLocks === 'y' || this.props.showZoomLocks === 'xy'))) && (
                    <ButtonY onClick={() => this.toggleBools('y')}>
                      {this.state.axisLocked.y ? <LockedZoomSVG /> : <UnlockedZoomSVG />}
                    </ButtonY>
                  )}
                  {(this.props.lockYAxis || (this.props.showZoomLocks && this.props.showZoomLocks === 'x')) ? '' : (
                    <ButtonY top="15px" left="-55px" onClick={() => this.changeYAxisDomain()}>
                      <EditIcon width={12} height={12} fill={this.state.yAxisModalOpen ? '#91b846' : '#999B95'} />
                    </ButtonY>
                  )}
                  {(this.props.isMultiSetActive) && (
                    <MultiSetItem
                      type="checkbox"
                      top="110px"
                      left="-55px"
                      onClick={this.props.setMultiSetItem}
                      value={this.props.isMultiSetItem}
                    />
                  )}
                </Fragment>
              )}

            </LineChartContainer>
            {this.props.paginationLoading && (
              <Loader>
                <LoadingSvg />
              </Loader>
            )}
          </div>
          {this.props.contextChart && !this.props.miniMode && (
            <div style={{ position: 'relative', overflow: 'hidden' }}>
              <ContextChart
                ref={(node) => { this.lineContext = node; }}
                type="context"
                autoFill
                height={this.props.contextHeight || this.state.contextHeight}
                xAxis={this.props.xAxis}
                chartName={`${this.props.chartName}-ctx`}
                data={this.props.data}
                range={this.props.range}
                chartType={this.props.type}
                selectedRange={this.props.selectedRange}
                brushCb={this.props.brushCb}
                colors={this.props.colors}
                xUnit={this.props.xUnit}
                ampType={this.props.ampType}
                applyGradient={this.props.applyGradient}
                // brushSelected={this.props.brushSelected}
                xScale={this.state.xScale}
                yScale={this.state.yScale}
                activeData={this.props.activeData}
                utilCtx={this.props.type === 'utilization'} // TODO: switch logic
                parent={this} // TODO: fix to separate props
                xAxisLocked={this.state.axisLocked.x}
                resetChartZoom={this.resetChartZoom}
                multiBrush={this.props.multiBrush}
                disableInitialBrush={this.props.disableInitialBrush}
                tag_type={this.props.tag_type}
                hierarchyViewPane={this.props.hierarchyViewPane}
              />
              {this.props.paginationLoading && (
                <Loader>
                  <LoadingSvg />
                </Loader>
              )}
            </div>
          )
          }
        </Fragment>
      );
    }
  }

  LineChartWrapper.propTypes = {
    data: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({
      x: PropTypes.oneOfType([PropTypes.object, PropTypes.number]).isRequired,
      y: PropTypes.number.isRequired
    }))).isRequired,
    activeData: PropTypes.array,
    chartName: PropTypes.string,
    contextChart: PropTypes.bool,
    applyGradient: PropTypes.bool,
    assetConditionLevels: PropTypes.number,
    range: PropTypes.array,
    domain: PropTypes.array,
    xAxisTicks: PropTypes.number,
    multiBrush: PropTypes.bool,
    height: PropTypes.number,
    width: PropTypes.number,
    hoveredLegendItem: PropTypes.number
  };

  LineChartWrapper.defaultProps = {
    activeData: [true],
    chartName: undefined,
    contextChart: false,
    assetConditionLevels: 3,
    applyGradient: false,
    range: [0, 10],
    domain: undefined,
    xAxisTicks: 6,
    multiBrush: false,
    height: undefined,
    width: undefined,
    hoveredLegendItem: null
  };

  LineChartWrapper.displayName = 'LineChartWrapper';

  return LineChartWrapper;
};

export const ContextChart = LineChart(LineContext);
